import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu, Moon, Settings, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/common/Wordmark";
import { NotificationBell } from "@/components/feed/NotificationBell";
import { ActionDot, menuBertanda, useUnreadNotifications } from "./ActionDot";
import { SchoolSwitcher } from "./SchoolSwitcher";

const useTheme = () => {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  const toggle = () => {
    const next = !dark;
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("sist-theme", next ? "dark" : "light");
    setDark(next);
  };
  return [dark, toggle];
};

const NavItems = ({ nav, platform, onClick, tanda }) => (
  <nav className="flex flex-col gap-1" aria-label="Navigasi utama">
    {nav.map((n) => (
      <NavLink key={n.to} to={n.to} end={n.end} onClick={onClick} data-testid={n.testId}
        className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
          platform
            ? isActive ? "bg-white text-[#0B3D91]" : "text-white/80 hover:bg-white/10 hover:text-white"
            : isActive ? "bg-[#0B3D91] text-white dark:bg-[#3B82F6]" : "text-foreground/75 hover:bg-muted"}`}>
        <n.icon className="h-4 w-4" aria-hidden /> {n.label}
        <ActionDot show={tanda.has(n.to)} testId={`nav-dot-${n.testId}`} className="ml-auto" />
      </NavLink>
    ))}
  </nav>
);

const Sidebar = ({ variant, spaceLabel, nav, onClick, tanda }) => {
  const platform = variant === "platform";
  return (
    <div className={`flex h-full flex-col gap-8 p-5 ${platform ? "bg-[#0B3D91] text-white" : "bg-card border-r"}`}>
      <div>
        <Wordmark inverted={platform} />
        <div className={`mt-4 inline-flex items-center gap-2 rounded-md px-2 py-1 text-[11px] font-bold uppercase tracking-[0.14em] ${platform ? "bg-[#E11D2E] text-white" : "bg-blue-50 text-[#0B3D91] dark:bg-blue-950 dark:text-blue-200"}`} data-testid="space-label">
          {spaceLabel}
        </div>
      </div>
      <NavItems nav={nav} platform={platform} onClick={onClick} tanda={tanda} />
    </div>
  );
};

// Tiga modul yang paling sering dibuka ditaruh di baris atas supaya tidak perlu membuka menu;
// sisanya tetap di balik garis tiga. Ikon hanya tampil bila peran memang berhak ke sana.
const SHORTCUTS = ["/sekolah/akademik", "/sekolah/penilaian", "/sekolah/presensi"];

const Shortcuts = ({ nav, tanda }) => {
  const items = SHORTCUTS.map((to) => nav.find((n) => n.to === to)).filter(Boolean);
  if (!items.length) return null;
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center gap-1" data-testid="header-shortcuts">
        {items.map((n) => (
          <Tooltip key={n.to}>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" asChild className="text-foreground/75" data-testid={`shortcut-${n.testId}`}>
                <NavLink to={n.to} aria-label={n.label} className="relative inline-flex">
                  <n.icon className="h-5 w-5" aria-hidden />
                  <ActionDot show={tanda.has(n.to)} testId={`shortcut-dot-${n.testId}`}
                    className="absolute -right-1 -top-1 ring-2 ring-background" />
                </NavLink>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{n.label}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
};

export const AppShell = ({ variant, spaceLabel, nav }) => {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dark, toggleTheme] = useTheme();
  const school = variant === "school";
  const belumDibaca = useUnreadNotifications();
  const tanda = menuBertanda(nav, belumDibaca);
  const doLogout = async () => {
    await logout();
    navigate("/masuk");
  };
  const nama = me?.user.full_name || "";
  const pengaturan = variant === "school" ? "/sekolah/pengaturan"
    : variant === "mitra" ? "/mitra/pengaturan" : "/pusat-kendali/pengaturan";
  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="hidden lg:block sticky top-0 h-screen"><Sidebar variant={variant} spaceLabel={spaceLabel} nav={nav} tanda={tanda} /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-[280px]">
          <SheetTitle className="sr-only">Menu navigasi</SheetTitle>
          <Sidebar variant={variant} spaceLabel={spaceLabel} nav={nav} onClick={() => setOpen(false)} tanda={tanda} />
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-col">
        {/* Header: hamburger + wordmark + notifications + theme + profile */}
        <header className="sticky top-0 z-30 flex items-center justify-between border-b bg-background/80 backdrop-blur-lg px-3 md:px-6 h-14">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden shrink-0" onClick={() => setOpen(true)} aria-label="Buka menu" data-testid="mobile-menu-button">
              <Menu className="h-5 w-5" />
            </Button>
            <Wordmark />
          </div>
          <div className="flex items-center gap-1">
            {/* Ruang sekolah butuh izin feed:read; Pusat Kendali dan Ruang Mitra selalu menampilkan lonceng. */}
            {(school ? me?.school_permissions?.includes("feed:read") : true) && <NotificationBell />}
            <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label={dark ? "Gunakan mode terang" : "Gunakan mode gelap"} data-testid="theme-toggle-button">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-9 shrink-0 rounded-full px-2" aria-label="Akun saya" data-testid="account-menu-button">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#0B3D91] text-xs font-bold text-white dark:bg-[#3B82F6]" data-testid="account-avatar">
                    {nama.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "S"}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64" data-testid="account-menu">
                <DropdownMenuLabel className="font-normal">
                  <p className="text-sm font-semibold leading-tight" data-testid="current-user-name">{nama}</p>
                  <p className="text-xs text-muted-foreground break-all">{me?.user.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="account-settings-link">
                  <Link to={pengaturan}><Settings className="mr-2 h-4 w-4" /> Pengaturan</Link>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={doLogout} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 md:px-10 md:py-10 max-w-7xl w-full"><Outlet /></main>
      </div>
    </div>
  );
};
